import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../apiBase";
const API_BASE = API;

/**
 * Read the pro JWT directly from localStorage so requests issued during the
 * very first render — before ProAuthContext has had a chance to install its
 * axios interceptor — still go out authenticated. Mirrors the key used by
 * components/ProAuthContext.jsx (`mc_pro_token`).
 */
const authHeader = () => {
  try {
    const t = localStorage.getItem("mc_pro_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch {
    return {};
  }
};

/**
 * useStudioDraft — minimal store + debounced PATCH driver for a single draft.
 *
 *   const { answers, services, brand, status, save, publish, isSaving, isPublishing } =
 *     useStudioDraft(draftId);
 *
 * Behaviour:
 *   - Loads the draft once at mount via GET /api/studio/drafts/{id}.
 *   - Provides setters that update local state IMMEDIATELY (no perceived lag)
 *     and schedule a debounced PATCH 700 ms later.
 *   - Coalesces concurrent updates into a single PATCH payload.
 *   - Exposes `publish()` which POSTs to /publish, then mutates status locally.
 *
 * NOTE: this hook is intentionally NOT a context provider. The sandbox only
 *       mounts one draft at a time so plumbing through props is sufficient.
 */
export default function useStudioDraft(draftId) {
  const [answers, setAnswers] = useState({});
  const [services, setServicesState] = useState([]);
  const [brand, setBrandState] = useState({});
  const [status, setStatus] = useState("draft"); // "draft" | "published" | "loading" | "error"
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // ---- Pending PATCH payload (coalesced) ----------------------------------
  const pendingRef = useRef({});
  const timerRef = useRef(null);
  const draftIdRef = useRef(draftId);
  draftIdRef.current = draftId;

  // Initial load
  useEffect(() => {
    if (!draftId) return;
    let alive = true;
    setStatus("loading");
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/studio/drafts/${draftId}`, { headers: authHeader() });
        if (!alive) return;
        setAnswers(data.answers || {});
        setServicesState(data.services || []);
        setBrandState(data.brand || {});
        setStatus(data.status || "draft");
      } catch (e) {
        if (!alive) return;
        setLoadError(e?.response?.data?.detail || "Impossible de charger le brouillon.");
        setStatus("error");
      }
    })();
    return () => { alive = false; };
  }, [draftId]);

  const flushPending = useCallback(async () => {
    const payload = pendingRef.current;
    pendingRef.current = {};
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!draftIdRef.current || Object.keys(payload).length === 0) return;
    setIsSaving(true);
    try {
      await axios.patch(`${API_BASE}/studio/drafts/${draftIdRef.current}`, payload, { headers: authHeader() });
    } catch (e) {
  console.warn("[studio draft] auto-save failed:", e?.response?.data?.detail || e.message);
    } finally {
      setIsSaving(false);
    }
  }, []);

  const schedulePatch = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flushPending, 700);
  }, [flushPending]);

  // Flush on unmount / visibility change so we never lose a pending edit.
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === "hidden") flushPending(); };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("beforeunload", flushPending);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("beforeunload", flushPending);
      flushPending();
    };
  }, [flushPending]);

  // ---- Public setters ------------------------------------------------------
  const setAnswer = useCallback((blockId, qId, value) => {
    setAnswers((prev) => {
      const block = { ...(prev[blockId] || {}) };
      block[qId] = value;
      return { ...prev, [blockId]: block };
    });
    const pAns = { ...(pendingRef.current.answers || {}) };
    pAns[blockId] = { ...(pAns[blockId] || {}), [qId]: value };
    pendingRef.current.answers = pAns;
    schedulePatch();
  }, [schedulePatch]);

  const setBrand = useCallback((updater) => {
    setBrandState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
      pendingRef.current.brand = next;
      return next;
    });
    schedulePatch();
  }, [schedulePatch]);

  const setServices = useCallback((next) => {
    setServicesState(next);
    pendingRef.current.services = next;
    schedulePatch();
  }, [schedulePatch]);

  const publish = useCallback(async () => {
    if (!draftIdRef.current) return null;
    // Flush any pending answer first so the publish snapshots the latest state.
    await flushPending();
    setIsPublishing(true);
    try {
      const { data } = await axios.post(`${API_BASE}/studio/drafts/${draftIdRef.current}/publish`, null, { headers: authHeader() });
      setStatus(data.status || "published");
      return data;
    } finally {
      setIsPublishing(false);
    }
  }, [flushPending]);

  return {
    answers, services, brand, status,
    isSaving, isPublishing, loadError,
    setAnswer, setBrand, setServices,
    publish, flushPending,
  };
}
